import type * as React from 'react';
import { type PropsWithChildren, useEffect, useRef } from 'react';
import { getComponent } from '../services/components';
import type { LoginProps, UnauthorizedProps } from '../types/public/component-props';
import type { AuthorizationContext, InvokeType } from '../types/public/models';

type Props = PropsWithChildren<{
    statusCode: string;
    invokeType: InvokeType;
    authenticationToken: string | null;
    authorizationContext: AuthorizationContext;
    notAuthorizedMessage: string | null;
    tenantId: string;
    stateId: string;
    onLogin: (authenticationToken: string) => void;
}>;

/** Component that determines authentication behaviour */
const Authentication = ({
    statusCode,
    invokeType,
    authenticationToken,
    authorizationContext,
    notAuthorizedMessage,
    tenantId,
    stateId,
    onLogin,
    children,
}: Props) => {
    const hasNavigated = useRef<boolean>(false);

    const Login = getComponent('login') as React.ComponentType<LoginProps>;
    const Unauthorized = getComponent('unauthorized') as React.ComponentType<UnauthorizedProps>;

    // biome-ignore lint/correctness/useExhaustiveDependencies: Requires dedicated refactor to include all dependencies
    useEffect(() => {
        if (
            statusCode === '401' &&
            authorizationContext.loginUrl &&
            (!authenticationToken || invokeType === 'NOT_ALLOWED')
        ) {
            switch (authorizationContext.authenticationType.toUpperCase()) {
                case 'OAUTH':
                case 'OAUTH2':
                case 'SAML':
                case 'OIDC': {
                    // This is wrapped in an if because, for some reason, this can execute twice before the
                    // window navigation has happened. Which can cause the first navigation request to be cancelled
                    // which, if using SAML, can cause the relay state to be consumed, which can make an error happen
                    if (!hasNavigated.current) {
                        window.boomi.flow.isRedirecting = true;
                        window.location.href = authorizationContext.loginUrl;
                        hasNavigated.current = true;
                    }
                    break;
                }
            }
        }
    }, [
        hasNavigated,
        statusCode,
        authenticationToken,
        authorizationContext.loginUrl,
        authorizationContext.authenticationType,
    ]);

    if (statusCode === '403' || (statusCode === '401' && authenticationToken)) {
        // User has been authenticated but is not authorized
        return <Unauthorized notAuthorizedMessage={notAuthorizedMessage} />;
    }
    if (statusCode === '401' && authorizationContext.loginUrl) {
        // User has not been authenticated
        switch (authorizationContext.authenticationType.toUpperCase()) {
            case 'OAUTH':
            case 'OAUTH2':
            case 'SAML':
            case 'OIDC':
                // This is handled in the useEffect up above
                return null;
            default:
                return (
                    <Login
                        loginUrl={authorizationContext.loginUrl}
                        tenantId={tenantId}
                        stateId={stateId}
                        onLogin={onLogin}
                    />
                );
        }
    }

    return <>{children}</>;
};

export default Authentication;
